import { motion } from "framer-motion";
import { CheckCircle2, Smartphone, Code, Zap, Shield, Layers } from "lucide-react";
import { useState } from "react";
import { ContactModal } from "@/components/ContactModal";

const MobileAppDevelopmentServices = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <main className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Mobile App Development Services
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground mb-8"
          >
            Build powerful mobile apps with our expert development team. From MVP to enterprise apps, we deliver iOS and Android solutions that drive business growth.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Start Your App Project
          </motion.button>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Our Mobile App Development Process
          </motion.h2>
          <div className="space-y-6">
            {[
              { step: 1, title: "Discovery & Strategy", desc: "We analyze your business goals, target users, and market requirements to define product strategy." },
              { step: 2, title: "UI/UX Design", desc: "User-centered design with wireframes, prototypes, and polished interfaces for optimal user experience." },
              { step: 3, title: "Agile Development", desc: "Iterative development with regular sprints, continuous integration, and client feedback loops." },
              { step: 4, title: "Testing & QA", desc: "Comprehensive testing including unit tests, integration tests, and user acceptance testing." },
              { step: 5, title: "Launch & Deployment", desc: "App store submission, deployment coordination, and launch support." },
              { step: 6, title: "Ongoing Support", desc: "Post-launch maintenance, updates, and performance monitoring." },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 items-start"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center text-coral font-bold">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Mobile App Development Services We Offer
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Smartphone, title: "iOS App Development", desc: "Native iOS apps built with Swift and latest Apple technologies." },
              { icon: Smartphone, title: "Android App Development", desc: "Native Android apps using Kotlin and modern Android development practices." },
              { icon: Layers, title: "Cross-Platform Development", desc: "React Native and Flutter apps for cost-effective multi-platform solutions." },
              { icon: Zap, title: "MVP Development", desc: "Rapid MVP development for startups to validate ideas quickly." },
              { icon: Code, title: "App Redesign", desc: "Modernize existing apps with improved UX and performance." },
              { icon: Shield, title: "Enterprise Apps", desc: "Scalable enterprise solutions with advanced security and integration." },
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <service.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-muted-foreground">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Our Tech Stack Expertise
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { tech: "React Native", desc: "Cross-platform development with native performance." },
              { tech: "Flutter", desc: "Beautiful, fast apps from a single codebase." },
              { tech: "Swift (iOS)", desc: "Native iOS development with Apple's latest frameworks." },
              { tech: "Kotlin (Android)", desc: "Modern Android development with Google's recommended language." },
              { tech: "Node.js", desc: "Scalable backend development for app APIs." },
              { tech: "Firebase", desc: "Real-time database, authentication, and analytics." },
            ].map((tech, index) => (
              <motion.div
                key={tech.tech}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-4 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-1">{tech.tech}</h3>
                <p className="text-muted-foreground">{tech.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MVP vs Full Product */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            MVP vs Full Product Development
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="bg-card p-6 rounded-lg border border-gray-800"
            >
              <h3 className="font-semibold text-xl mb-4 text-coral">MVP Development</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" />Timeline: 8-12 weeks</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" />Budget: $15K-$50K</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" />Core features only</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" />Quick market validation</li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-card p-6 rounded-lg border border-gray-800"
            >
              <h3 className="font-semibold text-xl mb-4 text-coral">Full Product</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" />Timeline: 16-24 weeks</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" />Budget: $50K-$200K+</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" />Complete feature set</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" />Scalable architecture</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline & Pricing */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Development Timeline & Pricing
          </motion.h2>
          <div className="space-y-4">
            {[
              { type: "Simple App", timeline: "8-12 weeks", price: "$15K-$50K" },
              { type: "Medium App", timeline: "12-16 weeks", price: "$50K-$100K" },
              { type: "Complex App", timeline: "16-24 weeks", price: "$100K-$250K+" },
            ].map((item, index) => (
              <motion.div
                key={item.type}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-4 rounded-lg border border-gray-800 flex justify-between items-center"
              >
                <div>
                  <h3 className="font-semibold text-lg">{item.type}</h3>
                  <p className="text-muted-foreground">{item.timeline}</p>
                </div>
                <div className="text-xl font-bold text-coral">{item.price}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Post-Launch Support */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Post-Launch Support & Maintenance
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Shield, title: "Bug Fixes", desc: "Quick resolution of issues and crashes." },
              { icon: Zap, title: "Feature Updates", desc: "New features and functionality additions." },
              { icon: Layers, title: "Performance Monitoring", desc: "Continuous monitoring and optimization." },
              { icon: Smartphone, title: "App Store Optimization", desc: "ASO for better visibility and downloads." },
            ].map((support, index) => (
              <motion.div
                key={support.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <support.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{support.title}</h3>
                <p className="text-muted-foreground">{support.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Why Choose Koech Creatives for Mobile App Development
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Code, title: "Full-Stack Expertise", desc: "End-to-end development from design to deployment." },
              { icon: Smartphone, title: "User-Centric Design", desc: "Focus on user experience and engagement." },
              { icon: Shield, title: "Transparent Communication", desc: "Regular updates and clear project visibility." },
              { icon: Zap, title: "Ongoing Support", desc: "Long-term partnership for app success." },
            ].map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4"
              >
                <reason.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{reason.title}</h3>
                  <p className="text-muted-foreground">{reason.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Ready to Build Your Mobile App?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground mb-8"
          >
            Let's turn your app idea into reality with expert development.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get a Free Consultation
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default MobileAppDevelopmentServices;
